import { useCallback, useEffect, useState } from 'react';
import { type ApiKey, type CreatedApiKey, type Project, type SessionUser, createApiKey, createProject, getCurrentUser, listApiKeys, listProjects, revokeApiKey, signOut } from '../lib/platform-api';

type Props = { onNavigate: (path: '/' | '/login') => void };

export function AdminPage({ onNavigate }: Props) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selected, setSelected] = useState<Project | null>(null);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [projectName, setProjectName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [keyName, setKeyName] = useState('Production');
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadProjects = useCallback(async (accountId: string) => {
    const data = await listProjects(accountId);
    setProjects(data);
    setSelected((current) => data.find((item) => item.id === current?.id) || data[0] || null);
  }, []);

  useEffect(() => {
    void (async () => {
      const current = await getCurrentUser();
      if (!current?.account_id) { onNavigate('/login'); return; }
      setUser(current);
      try { await loadProjects(current.account_id); } catch (error) { setMessage(error instanceof Error ? error.message : 'Gagal memuat project.'); }
      finally { setLoading(false); }
    })();
  }, [loadProjects, onNavigate]);

  useEffect(() => {
    if (!user?.account_id || !selected) { setKeys([]); return; }
    void listApiKeys(user.account_id, selected.id).then(setKeys).catch((error: unknown) => setMessage(error instanceof Error ? error.message : 'Gagal memuat API key.'));
  }, [selected, user?.account_id]);

  const addProject = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.account_id || !projectName.trim() || saving) return;
    setSaving(true); setMessage('');
    try {
      const project = await createProject(user.account_id, projectName.trim(), systemPrompt.trim());
      setProjects((items) => [project, ...items]); setSelected(project); setProjectName(''); setSystemPrompt('');
      setMessage('Project dibuat. Buat API key untuk menghubungkan aplikasi.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Gagal membuat project.'); }
    finally { setSaving(false); }
  };

  const addKey = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.account_id || !selected || !keyName.trim() || saving) return;
    setSaving(true); setMessage('');
    try {
      const key = await createApiKey(user.account_id, selected.id, keyName.trim());
      setKeys((items) => [key, ...items]); setCreatedKey(key); setKeyName('Production');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Gagal membuat API key.'); }
    finally { setSaving(false); }
  };

  const removeKey = async (key: ApiKey) => {
    if (!user?.account_id || !selected || saving || !window.confirm(`Cabut API key ${key.name}? Aplikasi yang memakainya akan berhenti mengakses API.`)) return;
    setSaving(true); setMessage('');
    try { await revokeApiKey(user.account_id, selected.id, key.id); setKeys((items) => items.filter((item) => item.id !== key.id)); }
    catch (error) { setMessage(error instanceof Error ? error.message : 'Gagal mencabut API key.'); }
    finally { setSaving(false); }
  };

  if (loading) return <main className="admin-page"><p>Memuat workspace…</p></main>;
  return <main className="admin-page">
    <header className="admin-header"><div><p className="admin-eyebrow">WORKSPACE ADMIN</p><h1>Project & API key</h1><p>Kelola akses aplikasi ke API chat. Kredensial Cloudflare tetap aman di server.</p></div><nav><button className="admin-link" onClick={() => onNavigate('/')}>Buka chat</button><button className="admin-link" onClick={() => void signOut().finally(() => onNavigate('/login'))}>Keluar</button></nav></header>
    {message && <p className="admin-message" role="status">{message}</p>}
    <section className="admin-grid">
      <aside className="admin-card"><h2>Project</h2><form onSubmit={(event) => void addProject(event)}><label>Nama project<input value={projectName} onChange={(event) => setProjectName(event.target.value)} placeholder="Contoh: Website produksi" required /></label><label>System prompt <small>(opsional)</small><textarea value={systemPrompt} onChange={(event) => setSystemPrompt(event.target.value)} placeholder="Instruksi khusus untuk project ini" /></label><button className="admin-primary" disabled={saving}>Buat project</button></form><div className="admin-project-list">{projects.map((project) => <button key={project.id} className={selected?.id === project.id ? 'active' : ''} onClick={() => setSelected(project)}><strong>{project.name}</strong><span>{project.slug}</span></button>)}{!projects.length && <p>Belum ada project.</p>}</div></aside>
      <section className="admin-card admin-workspace"><h2>{selected ? selected.name : 'Pilih atau buat project'}</h2>{selected && <><p className="admin-description">API key terikat ke project ini. Secret hanya ditampilkan sekali dan tidak pernah disimpan sebagai teks biasa.</p><form className="admin-key-form" onSubmit={(event) => void addKey(event)}><label>Nama API key<input value={keyName} onChange={(event) => setKeyName(event.target.value)} required /></label><button className="admin-primary" disabled={saving}>Generate key</button></form>{createdKey && <div className="admin-secret"><strong>Simpan sekarang — key ini tidak akan ditampilkan lagi.</strong><code>{createdKey.secret}</code><button onClick={() => void navigator.clipboard.writeText(createdKey.secret).then(() => setMessage('API key disalin.'))}>Salin key</button></div>}<div className="admin-key-list">{keys.map((key) => <article key={key.id}><div><strong>{key.name}</strong><code>{key.key_prefix}••••••••</code><small>{key.last_used_at ? `Terakhir dipakai ${new Date(key.last_used_at).toLocaleString('id-ID')}` : 'Belum digunakan'}</small></div><button className="admin-danger" disabled={saving} onClick={() => void removeKey(key)}>Cabut</button></article>)}{!keys.length && <p>Belum ada API key aktif.</p>}</div></>}</section>
    </section>
  </main>;
}
